import { MelodyLib } from '@tunasong/music-lib'
import type { Melody } from '@tunasong/schemas'
import type { FC, KeyboardEvent } from 'react'
import { beatX, rollBeats } from './melody-roll-geometry.js'
import styles from './velocity-lane.module.css'

/**
 * The velocity lane below the roll: one bar per note, aligned to the roll's time axis.
 *
 * §4.1 offers "a vertical drag with a modifier or a velocity lane below the roll". The lane wins:
 * a modifier drag is invisible until you know it exists, and it collides with the note-move drag
 * on the same target. A lane is its own affordance and gives every note a focusable control with
 * a spoken value, which a modifier drag cannot.
 *
 * Bars are drawn on the roll's time axis, not the note's own start, so the lane reads as a second
 * row of the same picture. Offsets are not drawn here - the lane is about loudness.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §4.1
 */

export interface VelocityLaneProps {
  melody: Melody
  onChange: (melody: Melody) => void
  selectedIndex?: number | null
  onSelect?: (index: number) => void
  className?: string
}

/** One arrow press. Ten steps across the range - fine enough to shape a phrase, coarse enough to reach an end. */
const STEP = 0.1

export const VelocityLane: FC<VelocityLaneProps> = props => {
  const { melody, onChange, selectedIndex, onSelect, className } = props
  const totalBeats = rollBeats(melody)

  if (melody.notes.length === 0) {
    return <div className={styles.empty}>No notes yet.</div>
  }

  const handleKeyDown = (event: KeyboardEvent<SVGRectElement>, index: number) => {
    const note = melody.notes[index]
    if (!note || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) {
      return
    }
    event.preventDefault()
    const next = note.velocity + (event.key === 'ArrowUp' ? STEP : -STEP)
    onChange(MelodyLib.setMelodyNoteVelocity(melody, index, next))
  }

  return (
    <div className={className ? `${styles.root} ${className}` : styles.root}>
      <svg
        className={styles.svg}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        role="group"
        aria-label="Note velocities"
      >
        <line className={styles.baseline} x1={0} y1={100} x2={100} y2={100} />
        {melody.notes.map((note, index) => {
          const height = note.velocity * 100
          const selected = selectedIndex === index
          return (
            <rect
              key={`vel-${note.onset}-${note.midi}-${index}`}
              data-testid="velocity-bar"
              data-selected={selected}
              className={selected ? `${styles.bar} ${styles.barSelected}` : styles.bar}
              role="slider"
              tabIndex={0}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(note.velocity * 100)}
              aria-label={`${MelodyLib.describeMelodyNote(note, melody)} - velocity ${Math.round(note.velocity * 100)}%`}
              x={beatX(note.onset, totalBeats)}
              y={100 - height}
              width={Math.max(0.8, beatX(note.duration, totalBeats))}
              height={height}
              onClick={() => onSelect?.(index)}
              onKeyDown={event => handleKeyDown(event, index)}
            />
          )
        })}
      </svg>
    </div>
  )
}
